import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Download, Eye } from 'lucide-react';
import { API } from '@/config/api';

export default function OwnerViewer({ token, user, onLogout }) {
  const navigate = useNavigate();
  const { udtId } = useParams();
  const [loading, setLoading] = useState(true);
  const [blobUrl, setBlobUrl] = useState('');
  const [fileName, setFileName] = useState('');

  const headers = useMemo(() => ({
    Authorization: `Bearer ${token}`
  }), [token]);

  useEffect(() => {
    let urlToRevoke = '';

    const load = async () => {
      try {
        setLoading(true);
        if (!udtId) throw new Error('Missing UDT ID');

        // Metadata (filename)
        const meta = await axios.get(`${API}/documents/detail/${udtId}`, { headers });
        setFileName(meta.data?.file_name || udtId);

        const res = await axios.get(`${API}/documents/open/${udtId}`, {
          headers,
          responseType: 'blob',
          timeout: 60000
        });

        if (!res.data || res.data.size === 0) {
          throw new Error('Document is empty or could not be loaded');
        }

        urlToRevoke = URL.createObjectURL(res.data);
        setBlobUrl(urlToRevoke);
      } catch (error) {
        console.error(error);
        toast.error(error.response?.data?.detail || error.message || 'Failed to open document');
      } finally {
        setLoading(false);
      }
    };

    load();

    return () => {
      if (urlToRevoke) URL.revokeObjectURL(urlToRevoke);
    };
  }, [udtId, headers]);

  const download = async () => {
    try {
      const res = await axios.get(`${API}/documents/open/${udtId}`, {
        headers,
        responseType: 'blob',
        timeout: 60000
      });

      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || `document-${udtId}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Download failed');
    }
  };

  return (
    <DashboardLayout user={user} token={token} onLogout={onLogout}>
      <div className="container mx-auto px-4 py-6 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => navigate('/dashboard/documents')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold" style={{ color: '#0F52BA' }}>Owner View</h1>
              <p className="text-sm text-gray-600">This view is not monitored and will not lock your document.</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={download}>
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
            {blobUrl && (
              <Button onClick={() => window.open(blobUrl, '_blank', 'noopener,noreferrer')} style={{ background: '#0F52BA' }}>
                <Eye className="w-4 h-4 mr-2" />
                Open in new tab
              </Button>
            )}
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{fileName || udtId}</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto"></div>
                <p className="mt-3 text-sm text-gray-600">Loading...</p>
              </div>
            ) : blobUrl ? (
              <iframe
                title="owner-document-viewer"
                src={blobUrl}
                className="w-full"
                style={{ height: '70vh', borderRadius: 8, border: '1px solid #E5E7EB' }}
              />
            ) : (
              <div className="text-center py-10 text-gray-600">
                Could not load document.
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}


